import { MembershipFragment } from '@occc/graphql';

type HasName = Pick<MembershipFragment, 'firstName' | 'lastName'>;

/**
 * Provides the member's full name (space-separated first and last name)
 * Returns undefined if neither are set.
 */
function getFullName(member: HasName): string | undefined {
  return [member.firstName, member.lastName].filter(Boolean).join(' ') || undefined;
}

/**
 * Provides the member's full name (per getFullName()) or returns a placeholder
 * when neither parts of their name are set. Used for display purposes.
 */
export function displayFullName(member: HasName): string {
  return getFullName(member) ?? 'No Name';
}

const MEMBERS_BASE_URL = '/members';
const MEMBERS_TRANSACTION_URLS = '/members-transactions';
const MEMBERS_POINT_CHANGE_URLS = '/members-point-changes';

export const routeCreator = {
  base: () => MEMBERS_BASE_URL,
  details: (memberId: string) => `${MEMBERS_BASE_URL}/${memberId}`,
  transaction: (activateTransactionId: string) =>
    `${MEMBERS_TRANSACTION_URLS}/${activateTransactionId}`,
  pointChange: (pointChangeId: string) => `${MEMBERS_POINT_CHANGE_URLS}/${pointChangeId}`,
};

class UserPreferences {
  private static KEY_ACTIVITY_SECTION = 'KEY_ACTIVITY_SECTION';
  private static KEY_TARGETING_SECTION = 'KEY_TARGETING_SECTION';
  private static KEY_OFFERS_SECTION = 'KEY_OFFERS_SECTION';
  private static KEY_WALLETS_SECTION = 'KEY_WALLETS_SECTION';

  private isSectionExpanded(key: string) {
    return localStorage.getItem(key) !== 'false';
  }

  private setSectionExpanded(key: string, expanded: boolean) {
    localStorage.setItem(key, String(expanded));
  }

  public isActivitySectionExpanded() {
    return this.isSectionExpanded(UserPreferences.KEY_ACTIVITY_SECTION);
  }

  public isTargetingSectionExpanded() {
    return this.isSectionExpanded(UserPreferences.KEY_TARGETING_SECTION);
  }

  public isOffersSectionExpanded() {
    return this.isSectionExpanded(UserPreferences.KEY_OFFERS_SECTION);
  }

  public isWalletsSectionExpanded() {
    return this.isSectionExpanded(UserPreferences.KEY_WALLETS_SECTION);
  }

  public setActivitySectionExpanded(expanded: boolean) {
    this.setSectionExpanded(UserPreferences.KEY_ACTIVITY_SECTION, expanded);
  }

  public setTargetingSectionExpanded(expanded: boolean) {
    this.setSectionExpanded(UserPreferences.KEY_TARGETING_SECTION, expanded);
  }

  public setOffersSectionExpanded(expanded: boolean) {
    this.setSectionExpanded(UserPreferences.KEY_OFFERS_SECTION, expanded);
  }

  public setWalletsSectionExpanded(expanded: boolean) {
    this.setSectionExpanded(UserPreferences.KEY_WALLETS_SECTION, expanded);
  }
}

export const userPreferences = new UserPreferences();
